import { Injectable, OnDestroy } from '@angular/core';
import { CONSTANTS } from '../../../Constants/Constants';
import { ReportPageOrientation } from '../../../models/reports/ReportPageOrientation';
import { ReportList } from '../../../models/dtos/Enums/ReportList';
import { TranslateService } from '@ngx-translate/core';
import { CommonDataService } from '../../common-data.service';
import { ReportsCommonService } from '../reports-common/reports-common.service';
import { IReportData } from '../../../models/reports/IReportData';
import { ITableData } from '../../../models/reports/ITableData';
import { IDataItem } from '../../../models/reports/IDataItem';
import { IData } from '../../../models/reports/IData';
import { ReportTextAlignment } from '../../../models/reports/ReportTextAlignment';
import {
  ControllerState, DesigoOpticState, IAreaChild, LaborData, LaborRates, Material,
  MaterialData, MiscData, OtPwrSalesOffices,
  SQWebArea, SQWebProject, SQWebSystem, SubSystem, SystemComponent, TypicalOfSystemHelper
} from '../../../models/dtos';
import { MultilingualCurrencyPipe } from '../../../pipes/multilingual-currency-pipe/multilingual-currency.pipe';
import { ICostByLevelData } from '../../../models/reports/ICostByLevelData';
import { roundToPrecision } from '../../../../types/number-extensions';
import { IndexDBService } from '../../common/indexDB.service';
import { LABOR_FIELDS } from '../../../models/reports/CostByLevelLaborFields';

type CostByLevelTotals = {
  materialCost: number;
  laborCost: number;
  subcontractorCost: number;
  miscellaneousCost: number;
  totalCost: number;
};

@Injectable()
export class CostByLevelService implements OnDestroy {

  private projectLevelDataList: ICostByLevelData[] = [];
  private areaLevelDataList: ICostByLevelData[] = [];
  private systemLevelDataList: ICostByLevelData[] = [];
  private typicalOfHelpers: Record<string, TypicalOfSystemHelper> = {};
  private readonly visibleTypicalOfCounts = new Map<string, number>();
  private materials: Record<string, MaterialData> = {};
  private labors: Record<string, LaborData> = {};
  private miscs: Record<string, MiscData> = {};
  private readonly laborRates = new Map<number, LaborRates>();
  private typicalOfLabel: string;

  constructor(
    private readonly commonDataService: CommonDataService,
    private readonly reportsCommonService: ReportsCommonService,
    private readonly translate: TranslateService,
    private readonly multilingualCurrencyPipe: MultilingualCurrencyPipe,
    private readonly indexDBService: IndexDBService
  ) { }
  async generateReport(reportId: ReportList, pageOrientation: ReportPageOrientation = CONSTANTS.REPORT_PAGEORIENTATION_PORTRAIT, internalReport: boolean = true): Promise<void> {
    const reportName = this.reportsCommonService.getSelectedReportName();
    const reportHeaderData = this.commonDataService.getReportHeaderData();
    const tableData = await this.generateTableData();
    const reportData: IReportData = {
      selectionName: this.translate.instant(reportName),
      reportTitle: this.translate.instant('COST_BY_LEVEL'),
      reportTitles: {
        data: this.reportsCommonService.generateReportTitles(reportHeaderData)
      },
      tableData,
      pageOrientation: pageOrientation,
      internalReport: internalReport
    };
    this.reportsCommonService.setSelectedReport(reportData);
  }

  async generateTableData(): Promise<ITableData[]> {
    const sqWebProject = this.commonDataService.getSQWebProject();
    const collections = sqWebProject.isItemVisible ? await Promise.all([
      this.indexDBService.getAllMaterialData(),
      this.indexDBService.getAllLaborData(),
      this.indexDBService.getAllMiscData()
    ]) : null;

    // Keep mutable report state inside the synchronous calculation phase.
    this.resetReportData();
    this.typicalOfLabel = this.translate.instant('TYPICAL_OF');
    if (!collections) {
      return [];
    }

    const [materials, labors, miscs] = collections;
    this.typicalOfHelpers = this.commonDataService.getTypicalOfHelpers();
    this.materials = materials.materialsCollection ?? {};
    this.labors = labors.laborsCollection ?? {};
    this.miscs = miscs.miscsCollection ?? {};
    for (const rates of this.commonDataService.getLaborMetaData()?.salesOfficeLaborRates ?? []) {
      this.laborRates.set(rates.salesOfficeId, rates);
    }

    try {
      this.processProjectLevelData(sqWebProject);
      this.processAreaAndSystemLevelData(sqWebProject.areas ?? []);
      this.applyTypicalOfMultipliers();

      const projectTotals = this.sumDataList(this.projectLevelDataList);
      const areaTotals = this.sumDataList(this.areaLevelDataList);
      const systemTotals = this.sumDataList(this.systemLevelDataList);
      return [
        this.buildProjectLevelTable(),
        this.buildLevelTable('AREAS', 'TOTAL_AREA_LEVEL', this.areaLevelDataList, areaTotals),
        this.buildLevelTable('SYSTEMS', 'TOTAL_SYSTEM_LEVEL', this.systemLevelDataList, systemTotals),
        this.buildTotalProjectTable(projectTotals, areaTotals, systemTotals)
      ];
    } finally {
      this.resetReportData();
    }
  }

  private processProjectLevelData(project: SQWebProject): void {
    const laborCost = this.getLaborCost(project.itemUniqueId);
    const subcontractorCost = this.getMiscCost(project.itemUniqueId, false);
    const miscCost = this.getProjectLevelMiscCost(project.itemUniqueId);

    this.projectLevelDataList.push({
      itemName: this.translate.instant('PROJECT_LEVEL'),
      typicalOfCount: CONSTANTS.NOT_APPLICABLE,
      materialCost: CONSTANTS.ZERO,
      laborCost: laborCost,
      subcontractorCost: subcontractorCost,
      miscellaneousCost: miscCost,
      totalCost: this.round(laborCost + subcontractorCost + miscCost),
      typicalSystemUniqueId: CONSTANTS.EMPTY_STRING,
      laborCostDXR: CONSTANTS.ZERO
    });
  }

  private getProjectLevelMiscCost(projectId: string): number {
    const estimateTab = this.commonDataService.getEstimateMetaData()?.estimateTab;
    const miscellaneous = this.getMiscCost(projectId, true, false);
    if (!estimateTab) {
      return this.round(miscellaneous);
    }

    const fees = [estimateTab.technologyFeeCost, estimateTab.mechFeeCost,
      estimateTab.subcontractorFeeCost, estimateTab.productivityReserveCost];
    return this.round(miscellaneous + fees.reduce((total, fee) => total + (fee ?? 0), 0) + (estimateTab.autoRiskCost ?? 0));
  }

  private processAreaAndSystemLevelData(children: readonly IAreaChild[]): void {
    for (const child of children) {
      if (!child.isItemVisible || child.nodeType !== CONSTANTS.NODETYPE_AREA) {
        continue;
      }

      const area = child as SQWebArea;
      this.processAreaLevelCost(area);
      for (const areaChild of area.children ?? []) {
        if (areaChild.isItemVisible && areaChild.nodeType === CONSTANTS.NODETYPE_BSETSYSTEM) {
          this.processSystemLevelCost(areaChild as SQWebSystem);
        }
      }
      this.processAreaAndSystemLevelData(area.children ?? []);
    }
  }

  private processAreaLevelCost(area: SQWebArea): void {
    const materialNode = this.getAreaMaterialNode(area);
    const areaMaterialCost = materialNode ? this.getMaterialCost(materialNode.itemUniqueId) : CONSTANTS.ZERO;
    const laborCost = this.getLaborCost(area.itemUniqueId);
    const subcontractorCost = this.getMiscCost(area.itemUniqueId, false);
    const miscCost = this.getMiscCost(area.itemUniqueId, true);

    this.areaLevelDataList.push({
      itemName: area.itemName,
      typicalOfCount: CONSTANTS.NOT_APPLICABLE,
      materialCost: areaMaterialCost,
      laborCost: laborCost,
      subcontractorCost: subcontractorCost,
      miscellaneousCost: miscCost,
      totalCost: this.round(areaMaterialCost + laborCost + subcontractorCost + miscCost),
      typicalSystemUniqueId: CONSTANTS.EMPTY_STRING,
      laborCostDXR: CONSTANTS.ZERO
    });
  }

  private processSystemLevelCost(system: SQWebSystem): void {
    const helperId = system.hasTypicalOf ? system.systemTypicalOfHelperUniqueId : '';
    if (helperId) {
      const count = (this.visibleTypicalOfCounts.get(helperId) ?? 0) + 1;
      this.visibleTypicalOfCounts.set(helperId, count);
      if (count > 1) {
        return;
      }
    }

    const levelId = this.typicalOfHelpers[helperId]?.typicalOfCollectionHolderId || system.itemUniqueId;
    const materialCost = this.getMaterialCost(levelId);
    let laborCost = this.getLaborCost(levelId);
    const laborCostDXR = system.isSystemDXR && system.isMaster ? this.getLaborCost(levelId, true) : 0;
    if (!helperId) {
      laborCost = this.round(laborCost + laborCostDXR);
    }
    const subcontractorCost = this.getMiscCost(levelId, false);
    const miscCost = this.getMiscCost(levelId, true);

    this.systemLevelDataList.push({
      itemName: system.itemName,
      typicalOfCount: helperId
        ? this.getConfiguredTypicalOfCount(helperId).toString()
        : CONSTANTS.ONE.toString(),
      materialCost: materialCost,
      laborCost: laborCost,
      subcontractorCost: subcontractorCost,
      miscellaneousCost: miscCost,
      totalCost: this.round(materialCost + laborCost + subcontractorCost + miscCost),
      typicalSystemUniqueId: helperId,
      laborCostDXR: helperId ? laborCostDXR : 0
    });
  }

  private getConfiguredTypicalOfCount(helperId: string): number {
    const length = this.typicalOfHelpers?.[helperId]?.typicalOfSystems?.length;
    return length && length > CONSTANTS.ZERO ? length : CONSTANTS.ONE;
  }

  private applyTypicalOfMultipliers(): void {
    for (const systemData of this.systemLevelDataList) {
      if (!systemData.typicalSystemUniqueId) {
        continue;
      }

      const count = this.visibleTypicalOfCounts.get(systemData.typicalSystemUniqueId) ?? CONSTANTS.ONE;

      systemData.materialCost = this.round(systemData.materialCost * count);
      systemData.laborCost = this.round(systemData.laborCost * count + systemData.laborCostDXR);
      systemData.subcontractorCost = this.round(systemData.subcontractorCost * count);
      systemData.miscellaneousCost = this.round(systemData.miscellaneousCost * count);
      systemData.totalCost = this.round(systemData.materialCost + systemData.laborCost
        + systemData.subcontractorCost + systemData.miscellaneousCost);
    }
  }

  private getAreaMaterialNode(area: SQWebArea): IAreaChild | undefined {
    const business = this.commonDataService.getBusiness();
    const supportsController = [CONSTANTS.BUSINESS_AUTOMATION, CONSTANTS.BUSINESS_ELECTRICAL, CONSTANTS.BUSINESS_MECHANICAL].includes(business);
    if (!supportsController && business !== CONSTANTS.BUSINESS_FIRE) {
      return undefined;
    }
    if (area.areaControllerState !== ControllerState.HasNoController) {
      const nodeType = business === CONSTANTS.BUSINESS_FIRE ? CONSTANTS.NODETYPE_PANEL
        : CONSTANTS.NODETYPE_CONTROLLER;
      const node = business === CONSTANTS.BUSINESS_FIRE ? area.panel : area.controller;
      return node ?? area.children?.find(child => child.nodeType === nodeType);
    }
    if (supportsController && area.areaDesigoOpticState !== DesigoOpticState.HasNoDesigoOptic) {
      return area.desigoOptics ?? area.children?.find(child => child.nodeType === CONSTANTS.NODETYPE_DESIGOOPTIC);
    }
    return undefined;
  }

  private getMaterialCost(levelId: string): number {
    const sumChildren = (children: SystemComponent[], quantity: number): number => {
      let total = 0;
      for (const child of children) {
        if (!child.isMaster) {
          continue;
        }
        if (child.nodeType === CONSTANTS.NODETYPE_SUBSYSTEM) {
          const subsystem = child as SubSystem;
          total += sumChildren(subsystem.children ?? [], quantity * subsystem.quantity);
        } else {
          const material = child as Material;
          if (material.materialType && material.quantity !== 0) {
            total += material.cost * material.quantity * quantity;
          }
        }
      }
      return total;
    };
    return this.round(sumChildren(this.materials[levelId]?.systemChildrenCollection ?? [], 1));
  }

  private getMiscCost(levelId: string, miscellaneous: boolean, rounded = true): number {
    const data = this.miscs[levelId];
    const items = (miscellaneous ? data?.miscellaneousCollection : data?.subcontractorCollection) ?? [];
    const total = items.reduce((sum, item) => sum + item.cost, 0);
    return rounded ? this.round(total) : total;
  }

  private getLaborCost(levelId: string, dxr = false): number {
    const data = this.labors[levelId];
    const offices = dxr ? data?.stdSalesOfficesOtPwrAdjustmentsDXRCollection : data?.stdSalesOfficesOtPwrAdjustmentsCollection;
    return this.round((offices ?? []).reduce((total, office) => total + this.getOfficeLaborCost(office), 0));
  }

  private getOfficeLaborCost(office: OtPwrSalesOffices): number {
    const rates = this.laborRates.get(office.salesOfficeId);
    if (!rates) {
      throw new Error(`Cost by Level: labor rates are missing for sales office ${office.salesOfficeId}.`);
    }
    const constants = this.commonDataService.getLaborMetaData().laborConstants;
    const overtime = rates.stdOverTimeRates;
    let total = 0;
    for (const field of LABOR_FIELDS) {
      let cost = 0;
      for (const row of office.overTimePWRAdjustments ?? []) {
        const hours = row[field.hours];
        if (!hours || hours === CONSTANTS.NA_LABOR_VALUE) {
          continue;
        }
        let rate = 0;
        switch (row.referenceNumber) {
          case CONSTANTS.STRAIGHT_TIME_CASE:
          case CONSTANTS.STRAIGHT_TIME_CASE_FR:
            rate = rates[field.standard];
            break;
          case CONSTANTS.OT_11X_TIME_CASE:
          case CONSTANTS.OT_15X_TIME_CASE:
          case CONSTANTS.OT_2X_TIME_CASE: {
            const otRates = row.referenceNumber === CONSTANTS.OT_11X_TIME_CASE ? overtime.overTime11X
              : row.referenceNumber === CONSTANTS.OT_15X_TIME_CASE ? overtime.overTime15X : overtime.overTime2X;
            rate = otRates[field.overtime];
            break;
          }
          case CONSTANTS.PWR_STRAIGHT_TIME_CASE:
          case CONSTANTS.PWR_STRAIGHT_TIME_CASE_FR:
            rate = field.pwr ? rates[field.pwr] : 0;
            break;
          case CONSTANTS.PWR_11X_TIME_CASE:
          case CONSTANTS.PWR_11X_TIME_CASE_FR:
            rate = field.pwr ? rates[field.pwr] * constants.pwrShiftDifferential11X : 0;
            break;
          case CONSTANTS.PWR_15X_TIME_CASE:
          case CONSTANTS.PWR_15X_TIME_CASE_FR:
            rate = field.pwr ? rates[field.pwr] * constants.pwR15X : 0;
            break;
          case CONSTANTS.PWR_2X_TIME_CASE:
          case CONSTANTS.PWR_2X_TIME_CASE_FR:
            rate = field.pwr ? rates[field.pwr] * constants.pwR2X : 0;
            break;
          default:
            throw new Error(`Cost by Level: unsupported overtime reference "${row.referenceNumber}".`);
        }
        cost += hours * rate;
      }
      total += cost;
    }
    return this.round(total);
  }

  private round(value: number): number {
    return roundToPrecision(value, this.commonDataService.getDecimalPrecision());
  }

  private formatCurrency(value: number): string {
    return this.multilingualCurrencyPipe.transform(value);
  }

  private createTitleRow(columns: { title: string, color?: string, width?: string }[]): IDataItem[] {
    return columns.map((col, index) => ({
      data: col.title,
      DataCellColor: col.color ?? CONSTANTS.REPORT_LIGHT_BLUE_COLOR,
      width: col.width,
      columnspan: CONSTANTS.ONE,
      hierarchyLevel: CONSTANTS.ZERO,
      alignment: index === 0 ? CONSTANTS.LEFT_ALIGNMENT : CONSTANTS.CENTER_ALIGNMENT
    }));
  }

  private createDataRow(dataList: {value: string, alignment?: ReportTextAlignment}[]): IData {
    return {
      data: dataList.map(val => ({
        data: val.value,
        columnspan: CONSTANTS.ONE,
        hierarchyLevel: CONSTANTS.ZERO,
        alignment: val.alignment ?? CONSTANTS.LEFT_ALIGNMENT
      }))
    };
  }

  private buildCommonHeaders(firstColumnTitle: string): { title: string, color?: string, width?: string }[] {
    return [
      { title: firstColumnTitle, color: CONSTANTS.REPORT_ORANGE_COLOR, width: '150' },
      { title: this.typicalOfLabel },
      { title: this.translate.instant('MATERIAL') },
      { title: this.translate.instant('LABOR') },
      { title: this.translate.instant('SUBS') },
      { title: this.translate.instant('MISC') },
      { title: this.translate.instant('TOTAL') }
    ];
  }

  private buildTableFooter(label: string, totals: CostByLevelTotals) {
    return [{
      headers: [
        { headerText: label, columnspan: CONSTANTS.ONE, alignment: CONSTANTS.LEFT_ALIGNMENT },
        { headerText: CONSTANTS.EMPTY_STRING, columnspan: CONSTANTS.ONE, alignment: CONSTANTS.CENTER_ALIGNMENT },
        { headerText: this.formatCurrency(totals.materialCost), columnspan: CONSTANTS.ONE, alignment: CONSTANTS.RIGHT_ALIGNMENT },
        { headerText: this.formatCurrency(totals.laborCost), columnspan: CONSTANTS.ONE, alignment: CONSTANTS.RIGHT_ALIGNMENT },
        { headerText: this.formatCurrency(totals.subcontractorCost), columnspan: CONSTANTS.ONE, alignment: CONSTANTS.RIGHT_ALIGNMENT },
        { headerText: this.formatCurrency(totals.miscellaneousCost), columnspan: CONSTANTS.ONE, alignment: CONSTANTS.RIGHT_ALIGNMENT },
        { headerText: this.formatCurrency(totals.totalCost), columnspan: CONSTANTS.ONE, alignment: CONSTANTS.RIGHT_ALIGNMENT }
      ]
    }];
  }

  private buildProjectLevelTable(): ITableData {
    const titleRow = this.createTitleRow(this.buildCommonHeaders(this.translate.instant('COST_BY_LEVEL_PROJECT')));

    const dataRows: IData[] = this.projectLevelDataList.map(item => this.createDataRow([
      { value: item.itemName },
      { value: item.typicalOfCount, alignment: CONSTANTS.CENTER_ALIGNMENT },
      { value: CONSTANTS.NOT_APPLICABLE, alignment: CONSTANTS.CENTER_ALIGNMENT },
      { value: this.formatCurrency(item.laborCost), alignment: CONSTANTS.RIGHT_ALIGNMENT },
      { value: this.formatCurrency(item.subcontractorCost), alignment: CONSTANTS.RIGHT_ALIGNMENT },
      { value: this.formatCurrency(item.miscellaneousCost), alignment: CONSTANTS.RIGHT_ALIGNMENT },
      { value: this.formatCurrency(item.totalCost), alignment: CONSTANTS.RIGHT_ALIGNMENT }
    ]));

    return {
      title: titleRow,
      data: dataRows,
      tableFooter: [],
      isTableBordered: true,
      isPageBreakAfter: false
    };
  }

  private buildLevelTable(titleKey: string, totalKey: string, rows: ICostByLevelData[], totals: CostByLevelTotals): ITableData {
    const titleRow = this.createTitleRow(this.buildCommonHeaders(this.translate.instant(titleKey)));

    const dataRows: IData[] = rows.map(item => this.createDataRow([
      { value: item.itemName },
      { value: item.typicalOfCount, alignment: CONSTANTS.CENTER_ALIGNMENT },
      { value: this.formatCurrency(item.materialCost), alignment: CONSTANTS.RIGHT_ALIGNMENT },
      { value: this.formatCurrency(item.laborCost), alignment: CONSTANTS.RIGHT_ALIGNMENT },
      { value: this.formatCurrency(item.subcontractorCost), alignment: CONSTANTS.RIGHT_ALIGNMENT },
      { value: this.formatCurrency(item.miscellaneousCost), alignment: CONSTANTS.RIGHT_ALIGNMENT },
      { value: this.formatCurrency(item.totalCost), alignment: CONSTANTS.RIGHT_ALIGNMENT }
    ]));

    return {
      title: titleRow,
      data: dataRows,
      tableFooter: this.buildTableFooter(this.translate.instant(totalKey), totals),
      isTableBordered: true,
      isPageBreakAfter: false
    };
  }

  private buildTotalProjectTable(
    projectTotals: CostByLevelTotals,
    areaTotals: CostByLevelTotals,
    systemTotals: CostByLevelTotals
  ): ITableData {
    const grandMaterial = projectTotals.materialCost + areaTotals.materialCost + systemTotals.materialCost;
    const grandLabor = projectTotals.laborCost + areaTotals.laborCost + systemTotals.laborCost;
    const grandSubs = projectTotals.subcontractorCost + areaTotals.subcontractorCost + systemTotals.subcontractorCost;
    const grandMisc = projectTotals.miscellaneousCost + areaTotals.miscellaneousCost + systemTotals.miscellaneousCost;
    const grandTotal = grandMaterial + grandLabor + grandSubs + grandMisc;

    const totalProjectFooter = [{
      headers: [
        { headerText: this.translate.instant('TOTAL_PROJECT'), columnspan: CONSTANTS.ONE, alignment: CONSTANTS.LEFT_ALIGNMENT, width: '220' },
        { headerText: this.formatCurrency(grandMaterial), columnspan: CONSTANTS.ONE, alignment: CONSTANTS.RIGHT_ALIGNMENT },
        { headerText: this.formatCurrency(grandLabor), columnspan: CONSTANTS.ONE, alignment: CONSTANTS.RIGHT_ALIGNMENT },
        { headerText: this.formatCurrency(grandSubs), columnspan: CONSTANTS.ONE, alignment: CONSTANTS.RIGHT_ALIGNMENT },
        { headerText: this.formatCurrency(grandMisc), columnspan: CONSTANTS.ONE, alignment: CONSTANTS.RIGHT_ALIGNMENT },
        { headerText: this.formatCurrency(grandTotal), columnspan: CONSTANTS.ONE, alignment: CONSTANTS.RIGHT_ALIGNMENT }
      ]
    }];

    return {
      tableHeader: [],
      title: [],
      data: [],
      tableFooter: totalProjectFooter,
      isTableBordered: true,
      isPageBreakAfter: false
    };
  }

  private sumDataList(dataList: ICostByLevelData[]): CostByLevelTotals {
    return dataList.reduce((acc, item) => ({
      materialCost: acc.materialCost + item.materialCost,
      laborCost: acc.laborCost + item.laborCost,
      subcontractorCost: acc.subcontractorCost + item.subcontractorCost,
      miscellaneousCost: acc.miscellaneousCost + item.miscellaneousCost,
      totalCost: acc.totalCost + item.totalCost
    }), {
      materialCost: CONSTANTS.ZERO,
      laborCost: CONSTANTS.ZERO,
      subcontractorCost: CONSTANTS.ZERO,
      miscellaneousCost: CONSTANTS.ZERO,
      totalCost: CONSTANTS.ZERO
    });
  }

  ngOnDestroy(): void {
    this.resetReportData();
  }

  private resetReportData(): void {
    this.projectLevelDataList = [];
    this.areaLevelDataList = [];
    this.systemLevelDataList = [];
    this.visibleTypicalOfCounts.clear();
    this.typicalOfHelpers = {};
    this.materials = {};
    this.labors = {};
    this.miscs = {};
    this.laborRates.clear();
  }
}
